import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import useEscapeLayer from "./useEscape";
import useIsMobile from "../hooks/useIsMobile";
import ReplyPanel from "./ReplyPanel";
import BackLink from "./BackLink";
import "./record/record.css";
import ListsGroup from "./record/ListsGroup";
import CallsGroup from "./record/CallsGroup";
import RoomGroup from "./rooms/RoomGroup";
import { recordView, pointOfContact, sourcePill } from "./record/situation.mjs";
import { recordZones, personZones } from "./record/zones.mjs";
import { scoreView } from "./record/scoreView.mjs";
import { nextModel, matchesOpenAction, NO_RECIPIENT } from "./record/nextModel.mjs";
import { meetingForOutcome, meetingEventId } from "./record/timelineView.mjs";
import { dealRows } from "./record/moneyModel.mjs";
import { writesFor } from "./record/endings.mjs";
import { fullName } from "./record/format.mjs";
import RecordHeader from "./record/RecordHeader";
import StickyLine from "./record/StickyLine";
import NextList from "./record/NextList";
import LeadBlock from "./record/LeadBlock";
import LatestConversation from "./record/LatestConversation";
import StoryCard from "./record/StoryCard";
import DealMoney from "./record/DealMoney";
import People from "./record/People";
import Fit from "./record/Fit";
import TheirLinkedIn from "./record/TheirLinkedIn";
import BehindScenes from "./record/BehindScenes";
import RecordTimeline from "./record/RecordTimeline";
import ProfileTab from "./record/ProfileTab";
import PhoneBar from "./record/PhoneBar";
import ConfirmCard from "./record/ConfirmCard";
import TranscriptDialog from "./record/TranscriptDialog";
import AssociatedPointer from "./record/lead/AssociatedPointer";
import { railSituation } from "./record/railModel.mjs";
import { unsent } from "./record/composerModel.mjs";

/*
 * THE LEAD RECORD (rebuilt against Gabriel's mockup, design/lead-record, 2026-10-01).
 *
 * One page, three sizes, one question: where do we stand with this company, and
 * what do I do next? It opens through a person, but most of what matters belongs
 * to their company (the company IS the deal), so the page loads the contact first
 * and then everything about the company in parallel.
 *
 * WHO OWNS WHAT, so nothing appears twice (the brief):
 *   header       who this is and where we stand, nothing else (RecordHeader)
 *   Next         every owed action, and the only place a button for one lives
 *   lead block   the facts this situation turns on, no action buttons
 *   rail groups  reference; a group leaves out what the lead block shows
 *   Profile      every stored fact, the one place repetition is allowed
 *
 * THIS FILE IS THE SHELL. It loads, decides the layout, computes the situation
 * and the models ONCE (record/situation.mjs, nextModel, scoreView, moneyModel,
 * zones) and hands every section the same `record` bundle and the same `ops`
 * handlers. No section re-derives "is this a client" from raw columns: fourteen
 * components each asking that is fourteen chances to disagree.
 *
 *   record = { id, contact, company, act, timeline: { rows, total, has_more,
 *              loadingOlder }, story, automations, booking, view, key, zones,
 *              score, next, deal, poc, source, layout, embedded, hostLabel,
 *              openAction, composer, ui, lastMeeting, now }
 *   ops    = { reload, patchCompany, patchContact, setProps, compose,
 *              closeComposer, control, dismiss, markOwedSent, createSlackChannel,
 *              meetingOutcome, openPerson, back, openStatus, closeStatus, openEnding,
 *              closeEnding, openTranscript, closeTranscript, runWrites,
 *              toggleBreakdown, loadOlder,
 *              rewriteStory, resolvePending, setTab, call }
 *   (each write returns its promise, so a control can show its own error beside
 *   itself, as the brief asks; every write reloads and tells the host.)
 *
 * LAYOUT FOLLOWS THE CONTAINER, NOT THE VIEWPORT. Today's panel is 560 wide on a
 * 1440 screen, Leads is a 660 overlay, Approvals is 540 to 720: a ResizeObserver
 * on the record's own root picks
 *   full   ≥ 1100: 780 main + 340 sticky rail with its own scroll
 *          (≥ 1560, a big office screen: three columns, the timeline beside
 *          the call and the story instead of under them, the rail wider)
 *   panel  < 1100: header, Next, lead block, latest conversation, story, the rail
 *          groups as one-line summaries, then Timeline · Profile
 *   phone  viewport ≤ 720: a full-screen sheet with its own bottom bar over the
 *          app's tabs
 *
 * THE PHONE SHEET IS PORTALED TO <body>. Its hosts (Today's and Leads' .crm-peek)
 * are position: fixed with a z-index of 40 and Today's slides in on a transform,
 * and either makes a stacking context: the sheet's own z-index 65 then only
 * counted inside the peek, so the app's tab bar (60) sat over the record's bottom
 * bar and the + button (55) put a second yellow on screen (2026-10-01, Corvid at
 * 375). From <body> the sheet, its composer and its bottom sheets stack against
 * the viewport. `inline` keeps it in place and in the panel layout whatever the
 * width: Approvals draws the record under its proposal, and a full-screen sheet
 * over that proposal hid the very form he was answering.
 *
 * Renders as a route (id from the URL) and as an embedded panel (id from a prop).
 * `onChange` tells a host that embeds the page about every write, so its row
 * updates while the panel is still open. `openAction` is the kind the host's row
 * was showing (Today, Follow-ups, Approvals): that line goes on top of Next with
 * its composer already open. `hostLabel` names where it was opened from
 * ("Today"), for the bar above the panel and the phone's back arrow.
 *
 * `guardRef`, when a host passes one, is filled with this record's guard: a host
 * about to unmount the record (another row clicked, its own composer opened)
 * calls guardRef.current(proceed), and an edited draft in here asks first
 * ("Throw away your reply to Ella?"). The host cannot ask the registry itself
 * with its own DOM node, because the phone sheet is not inside it.
 *
 * `focus` = "room" opens the company's Prospect Room group and scrolls to it:
 * Sales › Prospect rooms opens a record that way (src/rooms).
 */

const FULL_MIN = 1100;
const WIDE_MIN = 1560;

// The nearest ancestor that scrolls: the host's panel, or null for the page.
// The slim sticky line watches the header against it.
function scrollParent(el) {
  let node = el ? el.parentElement : null;
  while (node && node !== document.body) {
    const oy = window.getComputedStyle(node).overflowY;
    if (oy === "auto" || oy === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

// Where the one composer docks: under what it answers. A reply sits under the
// latest conversation; a first email, a welcome or a finance intro under the lead
// block that holds what it is about; anything else under Next.
const DOCK_UNDER_CONVERSATION = new Set(["reply_waiting", "agreement_reply", "nudge_due", "final_check_due", "pricing_stale", "agreement_stale", "call_followup", "wake_up_due", "re_engage"]);
const DOCK_UNDER_LEAD = new Set(["first_touch", "kickoff", "finance_intro"]);
function dockFor(kind, main) {
  if (DOCK_UNDER_CONVERSATION.has(kind) && main.includes("latest_conversation")) return "latest_conversation";
  if (DOCK_UNDER_LEAD.has(kind) || DOCK_UNDER_CONVERSATION.has(kind)) return "lead_block";
  return "next";
}

export default function ContactDetailPage({ contactId = null, embedded = false, onClose = null, onChange = null, openAction = null, hostLabel = null, inline = false, guardRef = null, focus = null }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const rootId = contactId || routeId;

  // THE PERSON STACK. Opening someone from People shows their record in the same
  // panel, with "← {company}" back; Esc closes the newest layer first.
  const [stack, setStack] = useState([rootId]);
  useEffect(() => setStack([rootId]), [rootId]);
  const id = stack[stack.length - 1];
  const nested = stack.length > 1;

  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);
  const [act, setAct] = useState(null);
  const [timeline, setTimeline] = useState({ rows: [], total: 0, has_more: false, loadingOlder: false });
  const [story, setStory] = useState(undefined);
  const [automations, setAutomations] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  // What NextList shows as receipts after a send. Kept here, not in the card: the
  // action is gone from the reloaded payload, so only the page remembers it.
  const [receipts, setReceipts] = useState([]);
  const [dismissedLocal, setDismissedLocal] = useState([]);
  const [composer, setComposer] = useState(null);
  const [ui, setUi] = useState({ status: false, score: false, ending: null, tab: "timeline", transcript: null });
  const [stuck, setStuck] = useState(false);
  // Each bump opens the Prospect Room group (the phone bar's More).
  const [roomSignal, setRoomSignal] = useState(0);
  const [width, setWidth] = useState(0);
  const isMobile = useIsMobile();

  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  composerRef.current = composer;
  const sentinelRef = useRef(null);
  const loadSeq = useRef(0);
  // Whose story the request in flight is for. The story keeps its own guard: it is
  // asked once per person and can take seconds when it rewrites, and gated by the
  // load token any write in those seconds (a dismiss, a send, a Held click) threw
  // the answer away and nothing asked again, so the card never came that visit.
  const storyFor = useRef(null);
  const autoOpened = useRef(false);

  // A new person: nothing of the last one may stay on screen.
  useEffect(() => {
    setContact(null);
    setCompany(null);
    setAct(null);
    setTimeline({ rows: [], total: 0, has_more: false, loadingOlder: false });
    setStory(undefined);
    setAutomations(null);
    setBooking(null);
    setReceipts([]);
    setDismissedLocal([]);
    setComposer(null);
    setUi({ status: false, score: false, ending: null, tab: "timeline" });
    setError("");
    autoOpened.current = false;
  }, [id]);

  /*
   * THE LOAD. The contact first (it says which company), then everything about
   * the company at once. The timeline, the actions and the company are what the
   * page stands on; the story, the automations and the booking link are additions
   * that may arrive later or fail without taking the page down. The story is read
   * on the first load only: its GET rewrites the cached text whenever the company
   * has something newer, and a send must not cost a rewrite (↻ is the way).
   */
  const load = useCallback(async ({ withStory = false } = {}) => {
    const token = ++loadSeq.current;
    const fresh = () => token === loadSeq.current;
    const c = await api(`/contacts/${id}`);
    if (!fresh()) return;
    const coId = c.company_id || null;
    const status = c.company && c.company.status;
    const bookable = status === "prospect" || status === "recycled";

    if (withStory) {
      storyFor.current = id;
      const mine = () => storyFor.current === id;
      (coId ? api(`/companies/${coId}/story`) : api(`/contacts/${id}/story`))
        .then((s) => mine() && setStory(s))
        .catch(() => mine() && setStory(null));
    }
    (coId ? api(`/companies/${coId}/automations`) : Promise.resolve(null))
      .then((a) => fresh() && setAutomations(a))
      .catch(() => fresh() && setAutomations(null));
    (bookable ? api(`/booking/contact/${id}`) : Promise.resolve(null))
      .then((b) => fresh() && setBooking(b))
      .catch(() => fresh() && setBooking(null));

    const [co, tl, a] = await Promise.all([
      coId ? api(`/companies/${coId}`) : Promise.resolve(null),
      coId
        ? api(`/companies/${coId}/timeline?limit=200`)
        : // No company: the person's own timeline, in the company timeline's shape
          // (every row theirs), so the same rows and words work for it.
          api(`/contacts/${id}/timeline?limit=200`).then((r) => {
            const me = { contact_id: c.id, first_name: c.first_name, last_name: c.last_name, email: c.email };
            const rows = (r.timeline || []).map((row) => ({
              ...row,
              contact_id: row.contact_id || c.id,
              contact_first_name: row.contact_first_name || c.first_name,
              contact_last_name: row.contact_last_name || c.last_name,
              direction: row.direction || (row.metadata && row.metadata.direction) || null,
              holders: row.subtype === "email" || row.subtype === "meeting" ? [me] : null,
            }));
            return { timeline: rows, total: rows.length, has_more: false };
          }),
      // The actions are an addition to a page that already stands on its facts:
      // a failure here leaves Next empty rather than the page blank.
      coId ? api(`/companies/${coId}/actions`).catch(() => null) : Promise.resolve(null),
    ]);
    if (!fresh()) return;
    setContact(c);
    setCompany(co);
    setAct(a);
    setTimeline({ rows: tl.timeline || [], total: tl.total || 0, has_more: !!tl.has_more, loadingOlder: false });
  }, [id]);

  useEffect(() => {
    load({ withStory: true }).catch((e) => setError(e.message));
  }, [load]);

  // After a send from the composer or any write on the page: everything reads
  // what just happened, and the host's row hears about it while the panel is open.
  const changed = useCallback(() => {
    if (onChange) onChange();
    return load().catch((e) => setError(e.message));
  }, [load, onChange]);

  /* ------------------------------------------------------------ layout */

  // The phone sheet lives in a portal, so moving between phone and not phone (a
  // rotation, a resized window) puts the root on a new node: measure that one.
  const portal = isMobile && !inline;
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [portal]);
  const layout = portal ? "phone" : width >= FULL_MIN ? "full" : "panel";

  // The slim sticky line, once the header has scrolled out of the host's scroll root.
  useEffect(() => {
    const s = sentinelRef.current;
    if (!s || typeof IntersectionObserver === "undefined") return undefined;
    const root = layout === "phone" ? scrollRef.current : scrollParent(rootRef.current);
    const io = new IntersectionObserver(
      ([e]) => {
        const top = e.rootBounds ? e.rootBounds.top : 0;
        setStuck(!e.isIntersecting && e.boundingClientRect.top < top + 1);
      },
      { root, threshold: 0 }
    );
    io.observe(s);
    return () => io.disconnect();
  }, [layout, contact]);

  /* ------------------------------------------------------------ the models, once */

  // "Now" moves with the data: every reload re-reads the day counts and the clocks.
  const now = useMemo(() => new Date(), [contact, company, act, timeline]);
  const view = useMemo(
    () => (contact ? recordView({ contact, company, act, now, entries: timeline.rows.length, layout }) : null),
    [contact, company, act, now, timeline.rows.length, layout]
  );
  const zones = useMemo(() => recordZones({ localZone: contact && contact.local_zone, myZone: act && act.my_zone }), [contact, act]);
  const poc = useMemo(() => pointOfContact(company), [company]);
  const score = useMemo(
    () => scoreView(company && company.lead_score, { contactId: contact && contact.id, pocId: poc && poc.id, now }),
    [company, contact, poc, now]
  );
  const next = useMemo(
    () => nextModel({ act, company, automations, openAction: nested ? null : openAction, receipts, dismissedLocal, zones, now }),
    [act, company, automations, openAction, nested, receipts, dismissedLocal, zones, now]
  );
  const deal = useMemo(
    // An associated colleague's rail is the company's: the situation the rows ask
    // about is the company's own (railSituation), so Gali's Deal and money points
    // at the New client block below instead of printing the fee twice.
    () => (company ? dealRows({ company, act, timeline: timeline.rows, situation: railSituation(view), now }) : []),
    [company, act, timeline.rows, view, now]
  );
  const formRow = useMemo(() => timeline.rows.find((r) => r.subtype === "form_submission") || null, [timeline.rows]);
  const source = useMemo(() => {
    const meta = (formRow && formRow.metadata) || {};
    // The form's own name when the row carries one ("Get a proposal"); the generic
    // "Website form" title says nothing the pill does not already say.
    const titled = formRow && formRow.title && !/^website form$/i.test(formRow.title) ? formRow.title : null;
    const formName = (meta.form && (meta.form.form_name || meta.form.name)) || meta.form_name || titled;
    return contact ? sourcePill(contact, { formName }) : null;
  }, [contact, formRow]);
  // The meeting Held / No-show answers: the one facts.last_meeting_at is about
  // (the server leaves "[Cancelled]" titles out of it), never simply the newest past
  // meeting row, which a cancelled or unrelated later invite can be.
  const lastMeeting = useMemo(
    () => meetingForOutcome(timeline.rows, { at: act && act.facts ? act.facts.last_meeting_at : null, now }),
    [timeline.rows, act, now]
  );

  /* ------------------------------------------------------------ ops */

  // EVERY CLOSE THAT COULD TAKE AN EDITED DRAFT WITH IT ASKS FIRST (reality.txt
  // [15]): the panel's ×, the phone's back arrow, the sticky line's ×, going back
  // up the person stack, opening someone else, a second composer replacing the
  // first. The composer registers itself while edited (composerModel.unsent);
  // when one inside this record holds, it shows "Throw away your reply to Ella?"
  // and runs `proceed` only after Discard or Save to Gmail.
  const guarded = useCallback((proceed) => {
    if (!unsent.ask(proceed, rootRef.current)) proceed();
  }, []);
  const close = useCallback(() => {
    if (onClose) guarded(onClose);
  }, [guarded, onClose]);
  useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = guarded;
    return () => {
      if (guardRef.current === guarded) guardRef.current = null;
    };
  }, [guardRef, guarded]);

  const call = useCallback((path, options = {}) => api(path, options), []);
  // The reload runs whether the write landed or not. An ending is several writes
  // in order (churn: the status, then its note), and when the second fails the
  // first has still changed the company: a page that only reloaded on success went
  // on saying Client after the churn, and its Confirm invited a retry that sent
  // the status again. The error still reaches the control that asked.
  const write = useCallback(async (fn) => {
    try {
      return await fn();
    } finally {
      await changed();
    }
  }, [changed]);

  const coId = company ? company.id : null;
  const ops = useMemo(() => {
    const o = {
      call,
      reload: changed,
      // The company's prospect room, for "Add to room" on the timeline and cards.
      roomCompany: company ? { id: company.id, name: company.name } : null,
      openRoom: () => setRoomSignal((n) => n + 1),
      patchCompany: (body) => write(() => api(`/companies/${coId}`, { method: "PATCH", body: JSON.stringify(body) })),
      patchContact: (cid, body) => write(() => api(`/contacts/${cid || id}`, { method: "PATCH", body: JSON.stringify(body) })),
      // properties_set (PR #154): merged into the bag, never replacing it.
      setProps: (target, tid, props) =>
        write(() => api(`/${target === "contact" ? "contacts" : "companies"}/${tid}`, { method: "PATCH", body: JSON.stringify({ properties_set: props }) })),
      compose: (desc) => {
        // Nobody to write to (a welcome on a client with no point of contact): said
        // beside the button that asked, rather than a yellow that does nothing.
        if (!desc || !desc.contact_id) throw new Error(NO_RECIPIENT);
        const key = desc.line_key || desc.kind;
        const open = composerRef.current;
        // The same line asked again: the draft on screen is that answer already.
        if (open && open.key === key) return;
        const person = ((company && company.contacts) || []).find((c) => String(c.id) === String(desc.contact_id)) || (contact && String(contact.id) === String(desc.contact_id) ? contact : null);
        const fresh = {
          key: desc.line_key || desc.kind,
          action_kind: desc.action_kind || desc.kind,
          contact_name: desc.contact_name || (person ? fullName(person) : null),
          booking_link: !!desc.booking_link,
          // The ADDRESSEE's clock: the offered times, their gate and the quote's
          // date are about whoever the email goes to, who is not always the
          // person whose record this is (Ella's reply on Priya's record).
          zones: personZones(person, zones),
          dock: dockFor(desc.action_kind || desc.kind, view ? view.main : []),
          action: {
            kind: desc.kind,
            verb: desc.verb || null,
            contact_id: desc.contact_id,
            contact_email: (person && person.email) || null,
            age_days: desc.age_days ?? null,
            activity_id: desc.activity_id || null,
            event_id: desc.event_id || null,
          },
        };
        // One composer: a second one replaces the first, after asking about it.
        guarded(() => setComposer(fresh));
      },
      closeComposer: () => setComposer(null),
      dismiss: (line) => {
        if (!line || !line.dismiss) return Promise.resolve();
        if (line.dismiss.local) {
          setDismissedLocal((d) => [...d, line.dismiss.kind]);
          return Promise.resolve();
        }
        const anchor = line.dismiss.anchor ? `?anchor=${encodeURIComponent(line.dismiss.anchor)}` : "";
        return write(() => api(`/companies/${coId}/actions/${line.dismiss.kind}${anchor}`, { method: "DELETE" }));
      },
      markOwedSent: () => write(() => api(`/companies/${coId}/owed`, { method: "DELETE" })),
      // The new client's internal channel, made in Slack with Gabriel and Lukasz in it.
      createSlackChannel: () => write(() => api(`/companies/${coId}/slack-channel`, { method: "POST", body: JSON.stringify({}) })),
      meetingOutcome: (eventId, outcome) =>
        write(() => api("/calendar/outcome", { method: "POST", body: JSON.stringify({ event_id: eventId, outcome, company_id: coId }) })),
      resolvePending: (pid, verdict) => write(() => api(`/pending-changes/${pid}/${verdict === "approve" ? "approve" : "dismiss"}`, { method: "POST", body: JSON.stringify({}) })),
      openPerson: (cid) => {
        if (!cid) return;
        guarded(() => setStack((st) => (String(st[st.length - 1]) === String(cid) ? st : [...st, cid])));
      },
      back: () => guarded(() => setStack((st) => (st.length > 1 ? st.slice(0, -1) : st))),
      openStatus: (opts) => {
        // From the sticky line or the phone's More: the pill is off screen, so the
        // header comes back first and the menu opens under it.
        if (opts && opts.scroll === true) {
          if (scrollRef.current) scrollRef.current.scrollTop = 0;
          else if (rootRef.current && rootRef.current.scrollIntoView) rootRef.current.scrollIntoView({ block: "start" });
        }
        setUi((u) => ({ ...u, status: !u.status, score: false }));
      },
      closeStatus: () => setUi((u) => ({ ...u, status: false })),
      openEnding: (endingId, preset = {}) => setUi((u) => ({ ...u, status: false, ending: { id: endingId, preset } })),
      closeEnding: () => setUi((u) => ({ ...u, ending: null })),
      // A recorded call's transcript, read over the record (record/TranscriptDialog),
      // opened at `at` seconds when a promise's time was clicked.
      openTranscript: (activityId, at = null) => activityId && setUi((u) => ({ ...u, transcript: { id: activityId, at } })),
      closeTranscript: () => setUi((u) => ({ ...u, transcript: null })),
      // An ending's writes (record/endings.mjs writesFor), in order, then one reload.
      runWrites: (writes) =>
        write(async () => {
          for (const w of writes) {
            await api(w.path, { method: w.method, body: w.body === undefined ? undefined : JSON.stringify(w.body) });
          }
        }),
      runEnding: (endingId, choice) => {
        const writes = writesFor(endingId, choice, { company, contact, now: new Date() });
        // Not a lead deletes the person on screen: there is nothing to reload into.
        //   - opened from People (nested): back to the company's record, which
        //     reloads on the id change and no longer lists them;
        //   - in a host's panel: the host closes it and refreshes its list;
        //   - as the page itself (/contacts/:id, which the board opens): the app's
        //     home, not history, which could put this contact's dead URL back.
        if (endingId === "not_a_lead") {
          return (async () => {
            for (const w of writes) await api(w.path, { method: w.method, body: JSON.stringify(w.body) });
            if (onChange) onChange();
            if (nested) setStack((st) => st.slice(0, -1));
            else if (embedded && onClose) onClose();
            else navigate("/");
          })();
        }
        return o.runWrites(writes);
      },
      toggleBreakdown: () => setUi((u) => ({ ...u, score: !u.score, status: false })),
      setTab: (tab) => setUi((u) => ({ ...u, tab })),
      loadOlder: async () => {
        const last = timeline.rows[timeline.rows.length - 1];
        if (!last || !coId) return;
        setTimeline((t) => ({ ...t, loadingOlder: true }));
        try {
          const page = await api(`/companies/${coId}/timeline?limit=200&before=${encodeURIComponent(last.occurred_at)}&before_id=${encodeURIComponent(last.id)}`);
          setTimeline((t) => ({ rows: [...t.rows, ...(page.timeline || [])], total: page.total || t.total, has_more: !!page.has_more, loadingOlder: false }));
        } catch (e) {
          setTimeline((t) => ({ ...t, loadingOlder: false }));
          throw e;
        }
      },
      rewriteStory: async () => {
        const s = await api(coId ? `/companies/${coId}/story` : `/contacts/${id}/story`, { method: "POST" });
        setStory(s);
        return s;
      },
      // One control of a Next line, wherever it was clicked (the card, the phone's
      // bar, the More sheet). Controls a card handles in place (Review, Move the
      // wake-up) are answered there; this returns false for them.
      control: (line, control) => {
        if (!control) return false;
        switch (control.id) {
          case "compose":
            if (control.blocked) throw new Error(control.blocked);
            o.compose({ ...control.compose, line_key: line.key, contact_name: line.contact_name });
            return true;
          case "mark_sent":
            return o.markOwedSent();
          case "slack_channel":
            return o.createSlackChannel();
          case "stop_reminders":
            return o.patchCompany({ recycle_until: null });
          case "remove_from_pipeline":
            o.openEnding("remove", { remind: `${(control.ending && control.ending.months) || 6}m` });
            return true;
          case "held":
          case "no_show": {
            // The key call_outcomes is written under (the timeline's own rule): a
            // meeting with neither an event id nor a row of its own has none.
            const ev = meetingEventId(lastMeeting);
            return ev ? o.meetingOutcome(ev, control.id) : false;
          }
          default:
            return false;
        }
      },
    };
    return o;
  }, [call, changed, write, guarded, coId, id, company, contact, view, zones, timeline.rows, lastMeeting, onChange, onClose, nested, embedded, navigate]);

  // Opened from a queue row: that row's action is on top with its composer open.
  // Once per record, and never when the host already holds a draft for it (Today
  // passes no openAction then, so there is never a second composer for one email).
  // Never on the phone: there the composer is a full-screen sheet, so opening it
  // at once hid the record he tapped the row to read (frame 1h draws the record
  // with its bar first), and drafted an email, a model call and an email_drafts
  // row, on every peek. The row's verb is the bar's yellow button there: one tap.
  useEffect(() => {
    if (autoOpened.current || !openAction || nested || !act || layout === "phone") return;
    const top = next.lines[0];
    if (!top || !matchesOpenAction(top.kind, openAction)) return;
    autoOpened.current = true;
    const c = top.controls && top.controls[0];
    if (c && c.id === "compose") ops.control(top, c);
  }, [openAction, nested, act, next, ops, layout]);

  // Esc, one layer at a time: the breakdown and the menu close before the person
  // opened from People, which closes before the panel itself (the host's layer).
  useEscapeLayer(nested, () => ops.back());

  /* ------------------------------------------------------------ render */

  const record = {
    id,
    contact,
    company,
    act,
    timeline,
    story,
    automations,
    booking,
    view,
    key: view ? view.key : null,
    zones,
    score,
    next,
    deal,
    poc,
    source,
    layout,
    embedded,
    hostLabel,
    openAction,
    composer,
    // Every send from this page, owed ones included (next.receipts leaves owed
    // out: its line stays in Next, reworded). Latest conversation reads it to show
    // what just went.
    sent: receipts,
    ui,
    lastMeeting,
    nested,
    now,
  };

  const backLabel = nested ? (company && company.name) || "Back" : hostLabel || (embedded ? "Back" : null);
  const fullHref = `/contacts/${id}`;
  // "Open full page" unmounts the panel and whatever it holds, so an edited draft
  // asks first; untouched, it is a plain link (a middle click still opens a tab).
  const openFull = (e) => {
    if (!unsent.holds(rootRef.current)) return;
    e.preventDefault();
    guarded(() => navigate(fullHref));
  };
  // The phone sheet over everything (see the header): the portal, or as it is.
  const wrap = (tree) => (portal && typeof document !== "undefined" ? createPortal(tree, document.body) : tree);

  const hostbar = embedded && layout !== "phone" && (
    <div className="rec-hostbar">
      {nested ? (
        <button type="button" className="rec-back" onClick={ops.back}>← {backLabel}</button>
      ) : (
        <span className="rec-hostbar__from">{hostLabel || ""}</span>
      )}
      <div className="rec-hostbar__right">
        <Link className="rec-hostbar__open" to={fullHref} onClick={openFull}>Open full page</Link>
        <button type="button" className="rec-close" title="Close (Esc)" aria-label="Close panel" onClick={close}>×</button>
      </div>
    </div>
  );

  const phonetop = layout === "phone" && (
    <div className="rec-phonetop">
      {nested || embedded || hostLabel ? (
        <button type="button" className="rec-back" onClick={nested ? ops.back : onClose ? close : () => guarded(() => window.history.back())}>← {backLabel || "Back"}</button>
      ) : (
        <BackLink />
      )}
      {embedded && <button type="button" className="rec-close" aria-label="Close" onClick={close}>×</button>}
    </div>
  );

  if (!contact || !view) {
    return wrap(
      <div ref={rootRef} className={`rec rec--${layout}`}>
        {phonetop}
        <div className="rec__shell">
          {hostbar}
          {!embedded && layout !== "phone" && <BackLink />}
          {error ? <p className="rec-err">{error}</p> : <p className="rec-sub">Loading…</p>}
        </div>
      </div>
    );
  }

  // On the phone the composer is its own full-screen sheet (fullScreen covers the
  // screen itself), so the wrapper is only the dock's hook.
  const composerEl = composer && (
    <div className="rec-composer" key={`composer-${composer.key}`}>
      <ReplyPanel
        action={composer.action}
        contactName={composer.contact_name}
        timeline={timeline.rows}
        people={company && company.contacts}
        zones={composer.zones || zones}
        booking={booking}
        bookingLink={composer.booking_link}
        fullScreen={layout === "phone"}
        onClose={() => setComposer(null)}
        onSent={(res) => {
          setReceipts((r) => [
            ...r,
            {
              kind: composer.action_kind,
              contact_id: composer.action.contact_id,
              contact_name: composer.contact_name,
              at: new Date().toISOString(),
              thread_url: (res && res.thread_url) || null,
            },
          ]);
          setComposer(null);
          changed();
        }}
      />
    </div>
  );
  const docked = (section) => (composer && composer.dock === section && layout !== "phone" ? composerEl : null);

  const sections = view.main.map((section) => {
    let el = null;
    if (section === "next") el = <NextList key="next" record={record} ops={ops} />;
    if (section === "lead_block") el = <LeadBlock key="lead" record={record} ops={ops} />;
    // A reply docks INSIDE the conversation it answers (frame 1d), under the
    // message, so what he is answering stays in view while he writes.
    if (section === "latest_conversation")
      return (
        <LatestConversation key={section} record={record} ops={ops} state={view.matrix.latest_conversation.state} owed={!!view.situation.overlays.reply_owed}>
          {docked(section)}
        </LatestConversation>
      );
    if (section === "story") el = <StoryCard key="story" record={record} ops={ops} />;
    return (
      <React.Fragment key={section}>
        {el}
        {docked(section)}
      </React.Fragment>
    );
  });

  const sheet = layout === "phone";
  const groups = view.rail.map((g) => {
    const props = { key: g.group, record, ops, state: g.state, line: g.line, sheet };
    if (g.group === "deal") return <DealMoney {...props} />;
    if (g.group === "people") return <People {...props} />;
    if (g.group === "fit") return <Fit {...props} />;
    if (g.group === "linkedin") return <TheirLinkedIn {...props} />;
    if (g.group === "behind_scenes") return <BehindScenes {...props} />;
    return null;
  });
  // Lists are the person's, not the situation's, so the group sits outside the
  // matrix and always last: reference about who they are, not about the deal.
  // The company's prospect room (src/rooms): one page for this prospect with
  // everything he chose to share. The company's, not the situation's, so it sits
  // outside the matrix like Lists; the Prospect rooms page opens a record on it.
  // On the phone it leads the list, and More opens it (ops.openRoom): further
  // down, under every card, it went unfound (Gabriel, 2026-10-18).
  if (company && company.id) {
    const room = <RoomGroup key="room" record={record} sheet={sheet} focus={focus === "room"} openSignal={roomSignal} />;
    if (layout === "phone") groups.unshift(room);
    else groups.push(room);
  }
  // Every call Fireflies recorded with the company (Meetings ▸ Calls), each one
  // opening there with its notes and transcript. The company's, like the room.
  if (company && company.id) groups.push(<CallsGroup key="calls" record={record} sheet={sheet} />);
  groups.push(<ListsGroup key="lists" record={record} sheet={sheet} />);

  // On the phone the tabs are one line that opens a sheet holding both (frame 1h),
  // so the page itself always shows the timeline's line, whatever tab was left on.
  const tabs = (
    <div className="rec-tabwrap" key="tabs">
      {ui.tab === "profile" && layout !== "phone" ? <ProfileTab record={record} ops={ops} /> : <RecordTimeline record={record} ops={ops} />}
    </div>
  );

  const ending = ui.ending && (
    <div className="rec-dialog__backdrop" onClick={ops.closeEnding}>
      <div className="rec-dialog" onClick={(e) => e.stopPropagation()}>
        <ConfirmCard record={record} ops={ops} endingId={ui.ending.id} preset={ui.ending.preset} onCancel={ops.closeEnding} onDone={ops.closeEnding} />
      </div>
    </div>
  );

  const transcript = ui.transcript && (
    <TranscriptDialog key={`${ui.transcript.id}:${ui.transcript.at}`} activityId={ui.transcript.id} at={ui.transcript.at} ops={ops} onClose={ops.closeTranscript} />
  );

  // An associated colleague's pointer is "one plain line under the header" (frame
  // 1i), above everything of the company's that follows, whichever section leads:
  // Ella's reply owed puts the conversation first, and the pointer still comes
  // before it.
  const header = (
    <>
      <RecordHeader record={record} ops={ops} />
      <div ref={sentinelRef} aria-hidden="true" />
      {view.key === "associated" && <AssociatedPointer record={record} ops={ops} />}
    </>
  );

  const sticky = stuck && (
    <div className="rec-stickywrap">
      <StickyLine record={record} ops={ops} onClose={embedded && onClose ? close : null} fullHref={embedded ? fullHref : null} onOpenFull={openFull} />
    </div>
  );

  if (layout === "full") {
    const wide = width >= WIDE_MIN;
    return (
      <div ref={rootRef} className={`rec rec--full${wide ? " rec--wide" : ""}`}>
        {sticky}
        {!embedded && <div className="rec__back"><BackLink /></div>}
        <div className="rec__shell">
          <div className="rec__main">
            {hostbar}
            {header}
            {sections}
            {!wide && tabs}
          </div>
          {wide && <div className="rec__history">{tabs}</div>}
          <aside className="rec__rail">{groups}</aside>
        </div>
        {ending}
        {transcript}
      </div>
    );
  }

  // The phone draws the rail groups and the timeline as rows of ONE card, each a
  // › that opens its bottom sheet (frame 1h); the panel keeps a card per group.
  const column = (
    <div className="rec__shell">
      {hostbar}
      {!embedded && layout !== "phone" && <BackLink />}
      {header}
      {sections}
      {layout === "phone" ? (
        <div className="rec__railstack rec-phonelist">
          {groups}
          {tabs}
        </div>
      ) : (
        <>
          {groups.length > 0 && <div className="rec__railstack">{groups}</div>}
          {tabs}
        </>
      )}
    </div>
  );

  if (layout === "phone") {
    return wrap(
      <div ref={rootRef} className="rec rec--phone">
        {phonetop}
        <div className="rec__scroll" ref={scrollRef}>
          {sticky}
          {column}
        </div>
        <PhoneBar record={record} ops={ops} />
        {composer && composerEl}
        {ending}
        {transcript}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="rec rec--panel">
      {sticky}
      {column}
      {ending}
      {transcript}
    </div>
  );
}
